import { Box3, LoadingManager, Mesh, Object3D, Scene, Vector3 } from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { MaterialManager, MaterialSummary } from '../materials/MaterialManager'
import type { VideoScreenController } from '../media/VideoScreenController'
import { disposeObject3D } from '../utils/disposeObject3D'

export interface SceneMetrics {
  objects: number
  meshes: number
  size: Vector3
  materialSummary: MaterialSummary
}

export class BedroomScene {
  private readonly scene: Scene
  private readonly loader: GLTFLoader
  private readonly materialManager: MaterialManager
  private readonly videoScreen: VideoScreenController
  private root: Object3D | null = null

  public constructor(
    scene: Scene,
    loadingManager: LoadingManager,
    materialManager: MaterialManager,
    videoScreen: VideoScreenController,
  ) {
    this.scene = scene
    this.loader = new GLTFLoader(loadingManager)
    this.materialManager = materialManager
    this.videoScreen = videoScreen
  }

  public async load(modelUrl: string): Promise<SceneMetrics> {
    const [, gltf] = await Promise.all([
      this.materialManager.load(),
      this.loader.loadAsync(modelUrl),
    ])

    const root = gltf.scene
    try {
      root.name = 'BedroomEnvironment'
      this.applyDefaultVisibility(root)
      const materialSummary = this.materialManager.apply(root)
      this.videoScreen.bind(root)

      let objects = 0
      let meshes = 0
      root.traverse((object) => {
        objects += 1
        if (!(object instanceof Mesh)) return
        meshes += 1
        object.frustumCulled = true
        if (!object.geometry.boundingSphere) object.geometry.computeBoundingSphere()
      })

      this.scene.add(root)
      root.updateMatrixWorld(true)
      const bounds = new Box3().setFromObject(root)
      const size = bounds.getSize(new Vector3())
      this.root = root
      return { objects, meshes, size, materialSummary }
    } catch (error) {
      this.scene.remove(root)
      this.videoScreen.dispose()
      const disposed = disposeObject3D(root)
      this.materialManager.forgetDisposedMaterials(disposed.materials)
      throw error
    }
  }

  private applyDefaultVisibility(root: Object3D): void {
    root.traverse((object) => {
      const defaultVisibility = object.userData.web_default_visible
      if (
        defaultVisibility === false
        || defaultVisibility === 0
        || defaultVisibility === 'false'
        || defaultVisibility === 'False'
      ) {
        object.visible = false
      }
    })
  }

  public setGroupVisible(group: string, visible: boolean): number {
    if (!this.root) return 0
    const normalizedGroupName = group.startsWith('GROUP_') ? group : `GROUP_${group}`
    let matches = 0
    this.root.traverse((object) => {
      if (object.userData.web_group !== group && object.name !== group && object.name !== normalizedGroupName) {
        return
      }
      object.visible = visible
      matches += 1
    })
    return matches
  }

  public listGroups(): string[] {
    if (!this.root) return []
    const groups = new Set<string>()
    this.root.traverse((object) => {
      const group = object.userData.web_group
      if (typeof group === 'string' && group) groups.add(group)
    })
    return [...groups].sort()
  }

  public dispose(): void {
    if (!this.root) return
    this.scene.remove(this.root)
    const disposed = disposeObject3D(this.root)
    this.materialManager.forgetDisposedMaterials(disposed.materials)
    this.root = null
  }
}
